import React, { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { trackEvent } from 'dibs-tracking';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { Link } from 'dibs-elements/exports/Link';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { createFragmentContainer, graphql } from 'react-relay';
import { PdpSharedStorytellingRelatedContent_storyTellingTab } from './__generated__/PdpSharedStorytellingRelatedContent_storyTellingTab.graphql';
import { PdpSharedStorytellingRelatedContent_item } from './__generated__/PdpSharedStorytellingRelatedContent_item.graphql';

import styles from './PdpSharedStorytellingRelatedContent.scss';

type ArticleType = NonNullable<
    NonNullable<
        NonNullable<
            PdpSharedStorytellingRelatedContent_storyTellingTab['supplementaryEditorial']
        >['articles']
    >[0]
>;
type RelatedCreatorType = NonNullable<
    PdpSharedStorytellingRelatedContent_storyTellingTab['relatedCreators']
>[0];

const WORDPRESS_THUMB = 95;
const WORDPRESS_MEDIUM = 276;
const WORDPRESS_FULL = 768;
const getHeaderId = (index: number): string => `wp-article-header-${index}`;

type Props = {
    storyTellingTab: PdpSharedStorytellingRelatedContent_storyTellingTab;
    item: PdpSharedStorytellingRelatedContent_item;
};

const PdpSharedStorytellingRelatedContentComponent: FC<Props> = ({ storyTellingTab, item }) => {
    const { supplementaryEditorial, relatedCreators } = storyTellingTab;
    const hasEditorial = !!supplementaryEditorial?.totalResults;
    const hasRelatedCreators = !!relatedCreators?.length;

    const getRelatedContentHeader = (): ReactNode => {
        const { vertical } = item || {};
        switch (vertical) {
            case 'art':
                return (
                    <FormattedMessage
                        defaultMessage="Related Artists"
                        id="pdp.resp.storytelling.relatedCreatorsHeader.art"
                    />
                );
            case 'fashion':
                return (
                    <FormattedMessage
                        defaultMessage="Related Designers"
                        id="pdp.resp.storytelling.relatedCreatorsHeader.fashion"
                    />
                );
            default:
                return (
                    <FormattedMessage
                        defaultMessage="Related Creators"
                        id="pdp.resp.storytelling.relatedCreatorsHeader.furnitureJewelry"
                    />
                );
        }
    };

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    {hasEditorial && (
                        <div className={styles.editorialContent}>
                            <Heading className={styles.readMoreHeader}>
                                <FormattedMessage
                                    defaultMessage="Read More"
                                    id="pdp.resp.storytelling.readMoreHeader"
                                />
                            </Heading>
                            {supplementaryEditorial?.articles?.map(
                                (article: ArticleType, index: number): ReactNode => (
                                    <Link
                                        ariaLabel={`Read more about ${article.title}`}
                                        ariaDescribedBy={`${getHeaderId(index)}`}
                                        buyerLinkType="standardLinkNoEffect"
                                        underline="none"
                                        href={article.link}
                                        target="_blank"
                                        dataTn={`wp-article-link-${index}`}
                                        className={styles.article}
                                        onClick={() => {
                                            trackEvent({
                                                category: 'ecommerce',
                                                action: 'promotion click',
                                                label: `editorial article - pdp: ${article.source} ${article.title}`,
                                            });
                                        }}
                                    >
                                        <div className={styles.articleImageWrapper}>
                                            <img
                                                className={styles.articleImage}
                                                src={article.imageUrl.thumb}
                                                srcSet={`
                ${article.imageUrl.thumb} ${WORDPRESS_THUMB}w,
                ${article.imageUrl.medium} ${WORDPRESS_MEDIUM}w,
                ${article.imageUrl.full} ${WORDPRESS_FULL}w
            `}
                                                sizes="(max-width: 320px) 100vw, 25vw"
                                                alt={article.title}
                                            />
                                        </div>
                                        <Heading
                                            className={styles.articleTitle}
                                            id={getHeaderId(index)}
                                            dangerouslySetInnerHTML={{ __html: article.title }}
                                        />
                                    </Link>
                                )
                            )}
                        </div>
                    )}
                    {hasRelatedCreators && (
                        <div className={styles.relatedCreatorsContent}>
                            <Heading className={styles.relatedCreatorsHeader}>
                                {getRelatedContentHeader()}
                            </Heading>
                            {relatedCreators?.map(
                                (relatedCreator: RelatedCreatorType): ReactNode =>
                                    relatedCreator ? (
                                        <SeoLink
                                            linkData={relatedCreator?.creatorBrowseUrl}
                                            dataTn={`storytelling-related-creator-link-${relatedCreator?.creatorName}`}
                                            className={styles.creatorLink}
                                            onClick={() => {
                                                trackEvent({
                                                    category: 'promo interaction',
                                                    action: 'related creator link click',
                                                    label: 'pdp',
                                                });
                                            }}
                                        >
                                            {relatedCreator?.creatorName}
                                        </SeoLink>
                                    ) : null
                            )}
                        </div>
                    )}
                </>
            )}
        </HeadingLevel>
    );
};

export const PdpSharedStorytellingRelatedContent = createFragmentContainer(
    PdpSharedStorytellingRelatedContentComponent,
    {
        item: graphql`
            fragment PdpSharedStorytellingRelatedContent_item on Item {
                vertical
            }
        `,
        storyTellingTab: graphql`
            fragment PdpSharedStorytellingRelatedContent_storyTellingTab on StoryTellingTab {
                relatedCreators {
                    creatorName
                    creatorBrowseUrl {
                        ...SeoLink_linkData
                    }
                }
                supplementaryEditorial {
                    totalResults
                    articles {
                        title
                        imageUrl {
                            thumb
                            medium
                            full
                        }
                        link
                        source
                    }
                }
            }
        `,
    }
);
